/**
 * @fileoverview System-web host adapter hook for the shared ParcelDataPanel.
 *   Maps the existing useDepartmentParcelData hook (which reads from
 *   geo_cache_files, import_runs, tenants, and background_jobs Dexie tables)
 *   into the unified ParcelDataPanelData + ParcelDataPanelActions shape
 *   expected by the shared ParcelDataPanel component.
 * @module system-web/hooks/useParcelDataPanelAdapter
 *
 * Responsibilities:
 * - Project useDepartmentParcelData output + tenant prop into
 *   ParcelDataPanelData.
 * - Wire system-admin action callbacks (saveTokenEntitlement,
 *   adminBypassUpdate, saveParcelConfig, triggerImport, cancelImport,
 *   refreshCacheFiles, estimateParcels) to the existing hook's mutations.
 *
 * Dependencies:
 * - ./useDepartmentParcelData (Dexie-backed reads + mutations)
 *
 * Architecture:
 * - Host adapter pattern: this hook is the system-web-specific bridge
 *   between the local-first data layer and the shared UI component.
 *   The shared ParcelDataPanel never reads Dexie or calls APIs — it
 *   receives all data and actions through this adapter.
 * - System-admin mode: the adapter exposes all admin actions (token
 *   editing, admin bypass update, reconfigure, estimate parcels, refresh
 *   cache files). It does NOT expose department-admin actions
 *   (checkForUpdate, runUpdate, toggleCreateContacts).
 *
 * Notes:
 * - The tenant prop provides updateTokens, parcelImport, and
 *   regridImportStatistics directly (the parent DepartmentPanel already has
 *   the tenant record from its own liveQuery). The hook supplements with the
 *   matched cache file and per-tenant import history.
 * - The import-level quality (importStats.dataQuality) comes from
 *   regridImportStatistics on the tenant document, projected through the
 *   shared `projectParcelImportStats` helper in @firesquirrel/ui-admin — the
 *   same helper tenant-web uses, since both hosts project the identical
 *   persisted ImportStatistics shape. This is the quality of the parcels
 *   actually imported into this department, which legitimately differs from
 *   the file-level quality on sourceFile because the department gets a
 *   filtered subset.
 *
 * @localFirst
 * Source of Truth: Dexie (geo_cache_files, import_runs, tenants,
 *   background_jobs) via useDepartmentParcelData + tenant prop
 * Write Flow: hook mutations → API → Dexie put → liveQuery emission
 * Sync Strategy: standard system pull for all collections; write-through
 *   for entitlement edits and admin bypass updates
 * Conflict Resolution: server-authoritative for all data
 */

import { useCallback, useMemo } from 'react';
import type { BackgroundJob } from '@firesquirrel/local-db';
import { projectParcelImportStats, resolveOwnerDataUpdate } from '@firesquirrel/ui-admin';
import { useDepartmentParcelData } from './useDepartmentParcelData';
import type {
  ParcelDataPanelData,
  ParcelDataPanelActions,
  ParcelDataConfig,
  ParcelSourceFile,
  ParcelImportStats,
  ParcelTokenEntitlement,
  ImportHistoryRow,
  DataQuality,
  ParcelConfigUpdate,
  ParcelConfigOverride,
  ParcelEstimateResult,
} from '@firesquirrel/ui-admin';

/**
 * @hook useParcelDataPanelAdapter
 * Purpose: Bridge useDepartmentParcelData → ParcelDataPanelData + Actions.
 * Props: tenant — the current department record (must contain _id;
 *   optionally parcelImport, jurisdiction.fipsCode, updateTokens).
 * Returns: { data, actions, importJob, dismissImportJob,
 *   saveParcelConfig, triggerImport, cancelImport } — the props for the
 *   shared ParcelDataPanel plus the host-specific handles the page needs
 *   to compose the reconfigure form and import progress display.
 * Side Effects: Delegates to useDepartmentParcelData (liveQuery
 *   subscriptions + mount-time cache file refresh).
 * Offline Behavior: Reads from Dexie; mutations require connectivity.
 *
 * @errorHandling
 * Strategy:   Errors from useDepartmentParcelData are surfaced as data.error.
 * User Impact: The ParcelDataPanel shows an inline error banner.
 * Logging:    Delegated to useDepartmentParcelData.
 */
export function useParcelDataPanelAdapter(tenant: any): {
  data: ParcelDataPanelData;
  actions: ParcelDataPanelActions;
  /** The active regridIngestion background job (null when none active). */
  importJob: BackgroundJob | null;
  /** Clear the import job display (used by ImportProgressDisplay dismiss). */
  dismissImportJob: () => void;
  /** Save full parcel config (used by the reconfigure form). */
  saveParcelConfig: (id: string, config: ParcelConfigUpdate) => Promise<boolean>;
  /** Trigger an admin-bypass import (used by the reconfigure form). */
  triggerImport: (id: string, overrides?: ParcelConfigOverride) => Promise<string | null>;
  /** Cancel an active import job (used by ImportProgressDisplay cancel). */
  cancelImport: (id: string, jobId?: string) => Promise<boolean>;
} {
  const {
    fips,
    cacheFile,
    importHistory,
    lastOwnerCsvUpdate,
    isLoading,
    error: hookError,
    refreshCacheFiles,
    saveTokenEntitlement,
    adminBypassUpdate,
    saveParcelConfig,
    triggerImport,
    cancelImport,
    estimateParcels,
    importJob,
    dismissImportJob,
    clearError,
  } = useDepartmentParcelData(tenant);

  // ── Extract tenant fields ──────────────────────────────────────────
  const updateTokens = tenant?.updateTokens as Record<string, unknown> | undefined;
  const tokensPerYear = typeof updateTokens?.tokensPerYear === 'number' ? updateTokens.tokensPerYear : 0;
  const tokensUsed = typeof updateTokens?.tokensUsed === 'number' ? updateTokens.tokensUsed : 0;
  const carriedOverBalance = typeof updateTokens?.carriedOverBalance === 'number' ? updateTokens.carriedOverBalance : 0;
  const renewalStart = typeof updateTokens?.renewalStart === 'string' ? updateTokens.renewalStart : null;
  const renewalEnd = typeof updateTokens?.renewalEnd === 'string' ? updateTokens.renewalEnd : null;
  const currentVersion = typeof updateTokens?.currentVersion === 'string' ? updateTokens.currentVersion : null;
  const lastUpdateDate = typeof updateTokens?.lastUpdateDate === 'string' ? updateTokens.lastUpdateDate : null;

  const parcelImport = tenant?.parcelImport as Record<string, unknown> | undefined;
  const importPath = typeof parcelImport?.path === 'string' ? parcelImport.path : null;
  const filterMode = typeof parcelImport?.filterMode === 'string' ? parcelImport.filterMode : null;
  const hazardLevels = Array.isArray(parcelImport?.hazardLevels) ? parcelImport.hazardLevels as string[] : [];
  const createOwnerContacts = parcelImport?.createOwnerContacts === true;
  const regridFileId = typeof parcelImport?.regridFileId === 'string' ? parcelImport.regridFileId : null;
  const regridFileName = typeof parcelImport?.regridFileName === 'string' ? parcelImport.regridFileName : null;

  const jurisdiction = tenant?.jurisdiction as Record<string, unknown> | undefined;
  const countyName = (jurisdiction?.county as Record<string, unknown>)?.name as string | undefined;
  const stateCode = (jurisdiction?.state as Record<string, unknown>)?.code as string | undefined;

  // ── Project config into unified shape ──────────────────────────────
  const config: ParcelDataConfig | null = useMemo(() => {
    if (!importPath && !fips) return null;
    return {
      path: importPath,
      regridFileId: regridFileId ?? fips ?? null,
      regridFileName: regridFileName ?? null,
      filterMode: filterMode,
      hazardLevels,
      createOwnerContacts,
      fipsCode: fips ?? regridFileId ?? null,
      county: countyName ?? null,
      state: stateCode ?? null,
    };
  }, [importPath, fips, regridFileId, regridFileName, filterMode, hazardLevels, createOwnerContacts, countyName, stateCode]);

  // ── Project source file from cache file ────────────────────────────
  const sourceFile: ParcelSourceFile | null = useMemo(() => {
    if (!cacheFile) return null;
    const dq: DataQuality | null = cacheFile.dataQuality ? {
      ownerNamePercent: cacheFile.dataQuality.ownerNamePercent ?? null,
      mailingAddressPercent: cacheFile.dataQuality.mailingAddressPercent ?? null,
      situsAddressPercent: cacheFile.dataQuality.situsAddressPercent ?? null,
      geometryValidPercent: cacheFile.dataQuality.geometryValidPercent ?? null,
    } : null;
    return {
      blobUri: cacheFile.blobUri ?? null,
      filename: cacheFile.filename ?? null,
      county: cacheFile.county ?? null,
      state: cacheFile.stateName ?? cacheFile.state ?? null,
      fipsCode: cacheFile.fipsCode ?? null,
      versionDate: cacheFile.versionDate ?? null,
      sizeBytes: cacheFile.sizeBytes ?? null,
      featureCount: cacheFile.featureCount ?? null,
      status: cacheFile.status ?? null,
      dataQuality: dq,
    };
  }, [cacheFile]);

  // ── Project import statistics from the tenant's regridImportStatistics ─
  // Read the authoritative statistics document off the tenant record via the
  // shared projection, exactly as tenant-web does. This must NOT be derived
  // from importHistory[0]: that list is a merge of several projections and
  // the newest entry was frequently the source cache file, which carries the
  // whole-county feature count and no import-level quality — which is what
  // made the Import Statistics section mirror the Source File section.
  // regridImportStatistics is written by the Regrid ingestion worker and is
  // the single record of what was actually imported for this department,
  // including quality computed over the filtered subset.
  const importStats: ParcelImportStats | null = useMemo(
    () => projectParcelImportStats(
      tenant?.regridImportStatistics as Record<string, unknown> | undefined,
      cacheFile?.versionDate ?? null,
    ),
    [tenant?.regridImportStatistics, cacheFile?.versionDate],
  );

  // ── Project token entitlement into unified shape ───────────────────
  const tokens: ParcelTokenEntitlement | null = useMemo(() => {
    const remaining = tokensPerYear - tokensUsed + carriedOverBalance;
    const hasEntitlement = tokensPerYear > 0 || renewalStart !== null;
    if (!hasEntitlement) return null;
    return {
      tokensPerYear,
      tokensUsed,
      remaining,
      carriedOverBalance,
      renewalStart,
      renewalEnd,
      currentVersion,
      lastUpdateDate,
      hasUpdate: false, // system-web doesn't run check-for-update
      latestVersion: null, // system-web doesn't run check-for-update
    };
  }, [tokensPerYear, tokensUsed, carriedOverBalance, renewalStart, renewalEnd, currentVersion, lastUpdateDate]);

  // ── Project import history into unified shape ──────────────────────
  const importHistoryRows: ImportHistoryRow[] = useMemo(() => {
    return importHistory.map((entry) => ({
      _id: entry._id,
      date: entry.date,
      parcels: entry.parcels,
      newCount: entry.newCount,
      updatesCount: entry.updatesCount,
      duration: entry.duration,
      version: entry.version,
      dataQuality: entry.dataQuality ? {
        ownerNamePercent: entry.dataQuality.ownerNamePercent ?? null,
        mailingAddressPercent: entry.dataQuality.mailingAddressPercent ?? null,
        situsAddressPercent: entry.dataQuality.situsAddressPercent ?? null,
        geometryValidPercent: entry.dataQuality.geometryValidPercent ?? null,
      } : null,
    }));
  }, [importHistory]);

  // ── Last owner-data update (Phase 5c, plan §C.5) ───────────────────
  // System-web sees two of the three sources: the tenant's Regrid import
  // statistics and completed parcelOwnerUpdate runs (both synced to the
  // system DB). Field-edit stamps live on tenant-DB entities which
  // system-web does not sync, so a 'field' winner is only surfaced in
  // tenant-web.
  const ownerDataUpdate = useMemo(
    () => resolveOwnerDataUpdate([
      { date: (tenant?.regridImportStatistics as Record<string, unknown> | undefined)?.importedAt as string | undefined, source: 'regrid' },
      { date: lastOwnerCsvUpdate, source: 'department-csv' },
    ]),
    [tenant?.regridImportStatistics, lastOwnerCsvUpdate],
  );

  // ── Assemble unified data ──────────────────────────────────────────
  const data: ParcelDataPanelData = useMemo(() => ({
    config,
    sourceFile,
    importStats,
    tokens,
    importHistory: importHistoryRows,
    ownerDataUpdate,
    isLoading,
    error: hookError,
    departmentName: tenant?.name ?? tenant?.departmentName ?? tenant?._id ?? null,
  }), [config, sourceFile, importStats, tokens, importHistoryRows, ownerDataUpdate, isLoading, hookError, tenant]);

  // ── Wire system-admin actions ──────────────────────────────────────
  const handleSaveTokenEntitlement = useCallback(async (tpy: number): Promise<boolean> => {
    return await saveTokenEntitlement(tenant._id, tpy);
  }, [saveTokenEntitlement, tenant._id]);

  const handleAdminBypassUpdate = useCallback(async (): Promise<boolean> => {
    const fileVersion = cacheFile?.versionDate ?? null;
    if (!fileVersion) return false;
    return await adminBypassUpdate(tenant._id, fileVersion);
  }, [adminBypassUpdate, tenant._id, cacheFile]);

  const handleTriggerImport = useCallback(async (overrides?: ParcelConfigOverride): Promise<string | null> => {
    return await triggerImport(tenant._id, overrides);
  }, [triggerImport, tenant._id]);

  const handleCancelImport = useCallback(async (): Promise<boolean> => {
    return await cancelImport(tenant._id);
  }, [cancelImport, tenant._id]);

  const handleSaveParcelConfig = useCallback(async (configUpdate: ParcelConfigUpdate): Promise<boolean> => {
    return await saveParcelConfig(tenant._id, configUpdate);
  }, [saveParcelConfig, tenant._id]);

  const handleEstimateParcels = useCallback(async (): Promise<ParcelEstimateResult | null> => {
    return await estimateParcels(tenant._id);
  }, [estimateParcels, tenant._id]);

  const actions: ParcelDataPanelActions = useMemo(() => ({
    clearError: clearError ?? (() => {}),
    saveTokenEntitlement: handleSaveTokenEntitlement,
    adminBypassUpdate: handleAdminBypassUpdate,
    triggerImport: handleTriggerImport,
    cancelImport: handleCancelImport,
    saveParcelConfig: handleSaveParcelConfig,
    refreshCacheFiles,
    estimateParcels: handleEstimateParcels,
  }), [clearError, handleSaveTokenEntitlement, handleAdminBypassUpdate, handleTriggerImport, handleCancelImport, handleSaveParcelConfig, refreshCacheFiles, handleEstimateParcels]);

  return {
    data,
    actions,
    importJob,
    dismissImportJob,
    saveParcelConfig,
    triggerImport,
    cancelImport,
  };
}
